import type { DatabaseDriver } from '@w/db'
import type { TursoWorkerTaskContext } from '@w/db-turso-browser-adapter/worker'
import { safe } from '@w/utils'

export const SESSION_USER_DB_TASK_ID = 'flashcards.session-user'
type SessionDriver = DatabaseDriver & {
  setSessionUser?: (userId: string | null) => void | Promise<void>
  runWorkerTask?: <T>(id: string, input: unknown) => Promise<T>
}

/** The session user each connection holds, recorded where the app sets it: it changes only through
 * `setWorkspaceSessionUser`, so writers read it here instead of a `SELECT turso_session_user()` per commit. */
const sessionUsers = new WeakMap<DatabaseDriver, string | null>()

/** Set the owning connection before any source write or replicated apply. */
export async function setWorkspaceSessionUser(driver: DatabaseDriver | null, userId: string) {
  const connection = safe(driver) as SessionDriver
  // Forget first: a failed or concurrent set leaves the next reader asking the engine, never a stale value.
  sessionUsers.delete(connection)
  if (connection.setSessionUser) await connection.setSessionUser(userId)
  else await safe(connection.runWorkerTask, 'Turso session-user setter required')<void>(SESSION_USER_DB_TASK_ID, { userId })
  sessionUsers.set(connection, userId)
}

/** The connection's session user: the engine is asked once per connection (a user the adapter set at open,
 * or none), then every later set goes through `setWorkspaceSessionUser`, which records it. */
export async function workspaceSessionUser(driver: DatabaseDriver) {
  const known = sessionUsers.get(driver)
  if (known !== undefined) return known
  const [session] = await driver.query<{ userId: string | null }>('SELECT turso_session_user() AS userId')
  const userId = session?.userId ?? null
  if (!sessionUsers.has(driver)) sessionUsers.set(driver, userId)
  return userId
}

export function run(ctx: TursoWorkerTaskContext<{ userId: string }>) {
  return setWorkspaceSessionUser(ctx.db, ctx.input.userId)
}
